<script setup lang="ts">
  import { computed } from 'vue';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import CloseIcon from '@/shared/assets/icons/CloseIcon.svg';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashCurrentColor.svg';
  import { IconImage, SelectInput, Tooltip } from '@/shared/ui';
  import type { ConditionalRuleAction } from '../../model/form-rules.store';

  interface SelectOption {
    id: string;
    name: string;
  }

  interface TargetItem {
    id: string;
    label: string;
  }

  const props = withDefaults(
    defineProps<{
      action: ConditionalRuleAction | '';
      targets: readonly TargetItem[];
      actionOptions: SelectOption[];
      actionDisabled?: boolean;
      deleteDisabled?: boolean;
      addDisabled?: boolean;
      confirmDisabled?: boolean;
      targetRemovalDisabled?: boolean;
      selectionActive?: boolean;
      faded?: boolean;
      actionSelectId?: string;
    }>(),
    {
      actionDisabled: false,
      deleteDisabled: false,
      addDisabled: false,
      confirmDisabled: false,
      targetRemovalDisabled: false,
      selectionActive: false,
      faded: false,
      actionSelectId: undefined,
    }
  );

  const emit = defineEmits<{
    (event: 'update-action', value: string | number): void;
    (event: 'delete'): void;
    (event: 'add-item'): void;
    (event: 'confirm'): void;
    (event: 'remove-target', targetId: string): void;
  }>();

  const actionOptionsWithPlaceholder = computed(() => [
    { id: '', name: 'Escolha a ação', disabled: true },
    ...props.actionOptions,
  ]);

  function emitTargetRemoval(targetId: string) {
    if (props.targetRemovalDisabled) return;
    emit('remove-target', targetId);
  }
</script>

<template>
  <div
    class="flex flex-col rounded-lg border border-gray-02 bg-white-02 p-4"
    :class="{ 'opacity-60': faded }"
    data-testid="then-block"
  >
    <div class="flex items-center justify-between">
      <label class="text-base font-medium text-gray-07" :for="actionSelectId">
        Então:
      </label>
      <Tooltip content="Deletar" placement="top">
        <button
          type="button"
          class="flex size-7 items-center justify-center rounded text-gray-05 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Excluir Então"
          :disabled="deleteDisabled"
          @click="emit('delete')"
        >
          <IconImage :src="TrashIcon" alt="Excluir Então" />
        </button>
      </Tooltip>
    </div>

    <SelectInput
      :id="actionSelectId"
      :model-value="props.action"
      :options="actionOptionsWithPlaceholder"
      :disabled="actionDisabled"
      label="Escolha a ação"
      container-class="relative mt-2"
      button-class="h-[44px] bg-white-01 border border-gray-02 text-sm text-gray-07 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
      max-height="220px"
      @update:model-value="emit('update-action', $event)"
    />

    <div class="my-3 border-t border-gray-02" />

    <section
      class="rounded-lg border p-3 transition-colors"
      :class="
        selectionActive
          ? 'border-[var(--theme-secondary-ink)]'
          : 'border-transparent'
      "
      data-testid="then-item-section"
    >
      <div class="flex items-center justify-between gap-3">
        <p class="text-sm font-medium text-gray-07">O item:</p>
        <div class="flex items-center gap-1">
          <Tooltip content="Adicionar item" placement="top">
            <button
              type="button"
              class="flex size-5 items-center justify-center rounded bg-[var(--theme-secondary)] text-white disabled:cursor-not-allowed disabled:bg-gray-02"
              aria-label="Adicionar item"
              :disabled="addDisabled"
              @click="emit('add-item')"
            >
              <IconImage :src="PlusIcon" alt="Adicionar item" />
            </button>
          </Tooltip>
          <Tooltip content="Confirmar" placement="top">
            <button
              type="button"
              class="flex size-5 items-center justify-center rounded bg-[var(--theme-secondary)] text-white disabled:cursor-not-allowed disabled:bg-gray-02"
              aria-label="Confirmar"
              :disabled="confirmDisabled"
              @click="emit('confirm')"
            >
              <IconImage :src="CheckMarkIcon" alt="Confirmar" />
            </button>
          </Tooltip>
        </div>
      </div>

      <p class="my-2.5 text-xs leading-4 text-gray-03">
        Clique no + para selecionar os itens que devem receber a ação dentro da página. Quando
        concluir, clique no 'check' para finalizar.
      </p>

      <div v-if="targets.length > 0" class="flex flex-col gap-2">
        <div
          v-for="target in targets"
          :key="target.id"
          class="flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-gray-02 bg-gray-08 px-4 text-sm text-gray-03 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]"
        >
          <span class="min-w-0 flex-1 truncate">{{ target.label }}</span>
          <button
            type="button"
            class="flex size-5 shrink-0 items-center justify-center text-gray-05 disabled:opacity-50 [&_*]:pointer-events-none"
            :class="{ 'cursor-not-allowed': targetRemovalDisabled }"
            aria-label="Remover item"
            :disabled="targetRemovalDisabled"
            @click="emitTargetRemoval(target.id)"
          >
            <IconImage :src="CloseIcon" alt="Remover item" />
          </button>
        </div>
      </div>
    </section>
  </div>
</template>
